import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Button } from '../components/ui';
import { BrandMark, WhalePod } from '../components/BrandMark';
import { markOnboarded } from '../lib/onboarding';

/**
 * Three-screen horizontal snap-scroll onboarding.
 *
 * The first slide carries the only illustration in the app — a
 * stylised candle with a dotted trail in warm amber. Hand-drawn with
 * Fraunces for "premium passbook" feel.
 */
export function Welcome() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  // Track scroll position to update dots + "next" button.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      const newIdx = Math.round(el.scrollLeft / w);
      setIndex(newIdx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const goNext = () => {
    const el = scroller.current;
    if (!el) return;
    const w = el.clientWidth;
    if (index < 2) {
      el.scrollTo({ left: (index + 1) * w, behavior: 'smooth' });
    } else {
      markOnboarded();
      nav('/how-it-works');
    }
  };

  const skip = () => {
    markOnboarded();
    nav('/');
  };

  const slides = [
    {
      title: t('welcome.s1_title'),
      body: t('welcome.s1_body'),
      illustration: <WhalePod size={280} />,
    },
    {
      title: t('welcome.s2_title'),
      body: t('welcome.s2_body'),
      illustration: <TransparencyLedger />,
    },
    {
      title: t('welcome.s3_title'),
      body: t('welcome.s3_body'),
      illustration: <WithdrawRing />,
    },
  ];

  return (
    <div className="relative flex h-full flex-col bg-tg-bg">
      <div className="flex items-center justify-between px-5 pt-4 safe-area-top">
        <BrandMark size="md" />
        <button
          type="button"
          onClick={skip}
          className="pb-eyebrow pb-focus-ring rounded-pb-sm px-2 py-1"
        >
          {t('welcome.skip')}
        </button>
      </div>

      <div
        ref={scroller}
        className="pb-snap flex flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
      >
        {slides.map((s, i) => (
          <div
            key={i}
            className="flex w-screen max-w-app shrink-0 flex-col items-center justify-center px-6"
          >
            <div className="flex h-[44vh] max-h-[320px] w-full items-center justify-center">
              {s.illustration}
            </div>
            <h1 className="pb-display mt-6 max-w-[340px] text-center text-[30px] leading-[1.08]">
              {s.title}
            </h1>
            <p className="mt-3 max-w-[320px] text-center text-[15px] leading-[1.5] text-tg-hint">
              {s.body}
            </p>
          </div>
        ))}
      </div>

      <div className="mx-auto flex w-full max-w-app flex-col items-center gap-5 px-6 pb-6 safe-area-bottom">
        <div className="flex items-center gap-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={clsx(
                'h-1.5 rounded-full transition-all',
                i === index ? 'w-6 bg-pb-accent' : 'w-1.5 bg-pb-border',
              )}
            />
          ))}
        </div>
        <Button onClick={goNext} fullWidth>
          {index < 2 ? t('welcome.next') : t('welcome.cta')}
        </Button>
      </div>
    </div>
  );
}

/* ── Bespoke illustrations ─────────────────────────────────────── */

function TransparencyLedger() {
  return (
    <svg viewBox="0 0 260 260" className="h-full w-full max-h-[320px] max-w-[320px]" aria-hidden>
      <g transform="translate(30, 50)">
        {[0, 1, 2, 3].map((i) => (
          <g key={i} transform={`translate(0, ${i * 42})`}>
            <rect
              x="0"
              y="0"
              width="200"
              height="34"
              rx="10"
              fill="var(--pb-paper)"
              stroke="var(--pb-border-soft)"
            />
            <circle cx="18" cy="17" r="6" fill="var(--pb-success-soft)" stroke="var(--pb-success)" />
            <rect x="32" y="11" width="70" height="5" rx="2.5" fill="var(--pb-border)" />
            <rect x="32" y="20" width="44" height="4" rx="2" fill="var(--pb-border-soft)" />
            <text
              x="185"
              y="22"
              textAnchor="end"
              fontFamily="Fraunces, serif"
              fontSize="13"
              fontWeight="500"
              fill="var(--pb-success)"
            >
              {['+$2.45', '+$0.88', '+$3.12', '+$1.64'][i]}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function WithdrawRing() {
  return (
    <svg viewBox="0 0 260 260" className="h-full w-full max-h-[320px] max-w-[320px]" aria-hidden>
      <defs>
        <radialGradient id="ring" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="var(--pb-accent)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="var(--pb-accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="130" cy="130" r="110" fill="url(#ring)" />
      <circle cx="130" cy="130" r="80" fill="none" stroke="var(--pb-accent-line)" strokeWidth="1.5" />
      <circle cx="130" cy="130" r="54" fill="var(--pb-paper-2)" stroke="var(--pb-border)" />
      <g transform="translate(130, 130)">
        <path
          d="M-22 -2 L22 -2 L14 -12 M22 -2 L14 8"
          stroke="var(--pb-accent)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <g
        fontFamily="Fraunces, serif"
        fontSize="12"
        fontWeight="500"
        fill="var(--tg-theme-hint-color,#708499)"
      >
        <text x="46" y="135">10м</text>
        <text x="200" y="135" textAnchor="end">·</text>
      </g>
    </svg>
  );
}
